
import React, { useState, useMemo, useEffect } from 'react';
import { ClipboardList, Plus, Search, ChevronRight, Calendar, Trash2, Download, CheckSquare, Square, Edit2, AlertCircle, AlertTriangle, User as UserIcon, Filter, ArrowUpDown, Globe, Inbox, BarChart3, Clock, CheckCircle2 } from 'lucide-react';
import { Attendance, User, Contact, UserRole, AttendanceStatus } from '../types';
import AttendanceDetailModal from './AttendanceDetailModal';
import SectionDashboard from './SectionDashboard';

interface AttendanceListProps {
  attendances: Attendance[];
  users: User[];
  contacts: Contact[];
  onUpdateAttendance: (id: string, updates: Partial<Attendance>) => void;
  onDeleteAttendance: (id: string) => void;
  onOpenCreate: () => void;
  currentUser: User;
  openAttendanceId?: string | null;
  onBulkDeleteAttendances?: (ids: string[]) => Promise<void>;
}

const AttendanceList: React.FC<AttendanceListProps> = ({ 
  attendances, 
  users, 
  contacts, 
  onUpdateAttendance, 
  onDeleteAttendance, 
  onOpenCreate, 
  currentUser, 
  openAttendanceId,
  onBulkDeleteAttendances
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedResponsibleId, setSelectedResponsibleId] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterUrgency, setFilterUrgency] = useState<string>('all');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [sourceFilter, setSourceFilter] = useState<'all' | 'public_form'>('all');

  const [selectedAttendanceId, setSelectedAttendanceId] = useState<string | null>(null);
  const [selectedAttendanceIds, setSelectedAttendanceIds] = useState<Set<string>>(new Set());
  const [listSortCol, setListSortCol] = useState<string>('');
  const [listSortDir, setListSortDir] = useState<'asc' | 'desc'>('asc');
  const [showDashboard, setShowDashboard] = useState(false);
  
  // Confirmações de exclusão
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [deleteSingleId, setDeleteSingleId] = useState<string | null>(null);

  const isLeadership = currentUser.role === UserRole.LEADERSHIP;

  useEffect(() => {
    if (openAttendanceId) {
      setSelectedAttendanceId(openAttendanceId);
    }
  }, [openAttendanceId]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case AttendanceStatus.DONE:
        return 'bg-emerald-50 text-emerald-600 border-emerald-100'; // Concluído (Verde)
      case AttendanceStatus.PENDING:
        return 'bg-red-50 text-red-600 border-red-100'; // Pendente (Vermelho)
      case AttendanceStatus.IN_PROGRESS:
      case AttendanceStatus.SCHEDULED:
        return 'bg-amber-50 text-amber-600 border-amber-100'; // Em andamento / Agendado (Amarelo)
      default:
        return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const externalUnviewedCount = useMemo(() =>
    attendances.filter(a => a.source === 'public_form' && !a.viewed_at).length,
    [attendances]
  );

  const filteredAttendances = useMemo(() => {
    let result = attendances.filter(a => {
      const contact = contacts.find(c => c.id === a.contactid);
      const matchesSearch = (a.demand || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
             (contact?.name || '').toLowerCase().includes(searchTerm.toLowerCase());

      const matchesResponsible = selectedResponsibleId === 'all' || a.responsible_id === selectedResponsibleId;
      const matchesStatus = filterStatus === 'all' || a.status === filterStatus;
      const matchesUrgency = filterUrgency === 'all' || a.urgency === filterUrgency;
      const matchesSource = sourceFilter === 'all' || a.source === sourceFilter;

      return matchesSearch && matchesResponsible && matchesStatus && matchesUrgency && matchesSource;
    });

    return result.sort((a, b) => {
      const getEffectiveDate = (item: Attendance) => {
        const created = new Date(item.created_at).getTime();
        const updated = item.updates?.length 
          ? Math.max(...item.updates.map(u => new Date(u.timestamp).getTime())) 
          : 0;
        return Math.max(created, updated);
      };
      
      const dateA = getEffectiveDate(a);
      const dateB = getEffectiveDate(b);
      
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });
  }, [attendances, searchTerm, selectedResponsibleId, contacts, filterStatus, filterUrgency, sortOrder, sourceFilter]);

  const sortedAttendances = useMemo(() => {
    if (!listSortCol) return filteredAttendances;
    return [...filteredAttendances].sort((a, b) => {
      let va: any, vb: any;
      if (listSortCol === 'title') { va = (a.demand || '').toLowerCase(); vb = (b.demand || '').toLowerCase(); }
      else if (listSortCol === 'status') { va = a.status; vb = b.status; }
      else if (listSortCol === 'requester') { const ca = contacts.find(c => c.id === a.contactid); const cb = contacts.find(c => c.id === b.contactid); va = (ca?.name || '').toLowerCase(); vb = (cb?.name || '').toLowerCase(); }
      else if (listSortCol === 'responsible') { const ra = users.find(u => u.id === a.responsible_id); const rb = users.find(u => u.id === b.responsible_id); va = (ra?.name || '').toLowerCase(); vb = (rb?.name || '').toLowerCase(); }
      else return 0;
      if (va < vb) return listSortDir === 'asc' ? -1 : 1;
      if (va > vb) return listSortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredAttendances, listSortCol, listSortDir, contacts, users]);

  const toggleListSort = (col: string) => {
    if (listSortCol === col) setListSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setListSortCol(col); setListSortDir('asc'); }
  };

  const selectedAttendance = useMemo(() => 
    attendances.find(a => a.id === selectedAttendanceId), 
    [selectedAttendanceId, attendances]
  );

  const toggleSelectAttendance = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newSelected = new Set(selectedAttendanceIds);
    if (newSelected.has(id)) newSelected.delete(id); else newSelected.add(id);
    setSelectedAttendanceIds(newSelected);
  };

  const toggleSelectAllAttendances = () => {
    if (selectedAttendanceIds.size === filteredAttendances.length && filteredAttendances.length > 0) {
      setSelectedAttendanceIds(new Set());
    } else {
      setSelectedAttendanceIds(new Set(filteredAttendances.map(a => a.id)));
    }
  };

  const handleExportAttendances = () => {
    const itemsToExport = selectedAttendanceIds.size > 0 
      ? attendances.filter(a => selectedAttendanceIds.has(a.id))
      : filteredAttendances;

    const headers = ['Título', 'Status', 'Urgência', 'Solicitante', 'Canal', 'Data', 'Responsável'];
    const csvContent = [
      headers.join(','),
      ...itemsToExport.map(a => {
        const contactName = contacts.find(c => c.id === a.contactid)?.name || 'Não identificado';
        const responsibleName = users.find(u => u.id === a.responsible_id)?.name || 'Não atribuído';
        return [
          `"${(a.demand || '').replace(/"/g, '""')}"`,
          `"${a.status}"`,
          `"${a.urgency}"`,
          `"${contactName}"`,
          `"${a.channel || ''}"`,
          `"${new Date(a.created_at).toLocaleDateString('pt-BR')}"`,
          `"${responsibleName}"`
        ].join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `atendimentos_export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleConfirmBulkDelete = async () => {
    if (onBulkDeleteAttendances) {
        await onBulkDeleteAttendances(Array.from(selectedAttendanceIds));
        setSelectedAttendanceIds(new Set());
    }
    setShowBulkDeleteConfirm(false);
  };

  const handleConfirmSingleDelete = () => {
    if (deleteSingleId) {
        onDeleteAttendance(deleteSingleId);
        setDeleteSingleId(null);
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500 relative">
      {/* BULK DELETE CONFIRMATION */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 z-[150] bg-navy-950/80 backdrop-blur-sm flex flex-col items-center justify-center p-8 text-center animate-in fade-in zoom-in-95">
           <div className="w-full max-w-md bg-white dark:bg-navy-900 p-8 rounded-[32px] border border-neutral-200 dark:border-navy-800 shadow-2xl" onClick={e => e.stopPropagation()}>
             <div className="w-20 h-20 bg-red-500/20 rounded-[24px] flex items-center justify-center mb-6 shadow-xl mx-auto text-red-500"><Trash2 size={32} /></div>
             <h3 className="text-3xl font-black text-navy-950 dark:text-white tracking-tighter mb-2">Excluir {selectedAttendanceIds.size} Atendimentos?</h3>
             <p className="text-slate-500 dark:text-slate-400 font-medium mb-8">Esta ação moverá os registros selecionados para a lixeira.</p>
             <div className="flex gap-4 justify-center">
                <button onClick={() => setShowBulkDeleteConfirm(false)} className="px-8 py-3 bg-neutral-100 dark:bg-navy-800 text-slate-600 dark:text-slate-300 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-neutral-200 transition-all">Cancelar</button>
                <button onClick={handleConfirmBulkDelete} className="px-8 py-3 bg-red-600 text-white rounded-xl font-black text-[10px] uppercase shadow-xl hover:scale-105 transition-all">Confirmar</button>
             </div>
           </div>
        </div>
      )}

      {/* SINGLE DELETE CONFIRMATION */}
      {deleteSingleId && (
        <div className="fixed inset-0 z-[150] bg-navy-950/80 backdrop-blur-sm flex flex-col items-center justify-center p-8 text-center animate-in fade-in zoom-in-95">
           <div className="w-full max-w-md bg-white dark:bg-navy-900 p-8 rounded-[32px] border border-neutral-200 dark:border-navy-800 shadow-2xl" onClick={e => e.stopPropagation()}>
             <div className="w-20 h-20 bg-red-500/20 rounded-[24px] flex items-center justify-center mb-6 shadow-xl mx-auto text-red-500"><AlertTriangle size={32} /></div>
             <h3 className="text-3xl font-black text-navy-950 dark:text-white tracking-tighter mb-2">Excluir Atendimento?</h3>
             <p className="text-slate-500 dark:text-slate-400 font-medium mb-8">O registro será movido para a lixeira.</p>
             <div className="flex gap-4 justify-center">
                <button onClick={() => setDeleteSingleId(null)} className="px-8 py-3 bg-neutral-100 dark:bg-navy-800 text-slate-600 dark:text-slate-300 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-neutral-200 transition-all">Cancelar</button>
                <button onClick={handleConfirmSingleDelete} className="px-8 py-3 bg-red-600 text-white rounded-xl font-black text-[10px] uppercase shadow-xl hover:scale-105 transition-all">Confirmar</button>
             </div>
           </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 px-2">
        <div>
          <h2 className="text-4xl font-black text-navy-950 dark:text-white tracking-tighter leading-none">Atendimentos</h2>
          <p className="text-slate-500 dark:text-slate-400 mt-2 font-medium">Gestão de demandas diretas da população.</p>
          <div className="flex items-center gap-2 mt-4">
            <button
              onClick={() => setSourceFilter('all')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all ${sourceFilter === 'all' ? 'bg-navy-950 dark:bg-emerald-600 text-white shadow-lg' : 'bg-white dark:bg-navy-900 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-navy-800 hover:bg-slate-50'}`}
            >
              <Inbox size={14} /> Todos
            </button>
            <button
              onClick={() => setSourceFilter('public_form')}
              className={`relative flex items-center gap-2 px-5 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all ${sourceFilter === 'public_form' ? 'bg-navy-950 dark:bg-emerald-600 text-white shadow-lg' : 'bg-white dark:bg-navy-900 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-navy-800 hover:bg-slate-50'}`}
            >
              <Globe size={14} /> Externos
              {externalUnviewedCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[9px] font-black rounded-full w-5 h-5 flex items-center justify-center shadow-lg">{externalUnviewedCount}</span>
              )}
            </button>
          </div>
        </div>
        <div className="flex items-center gap-3">
           <button onClick={() => setShowDashboard(!showDashboard)} className={`p-4 rounded-2xl transition-all shadow-sm border flex items-center gap-2 font-bold text-xs ${showDashboard ? 'bg-navy-950 dark:bg-parliament-600 text-white border-transparent' : 'bg-white dark:bg-navy-900 text-slate-500 dark:text-slate-400 border-neutral-200 dark:border-navy-800 hover:bg-neutral-50 dark:hover:bg-navy-800'}`} title="Dashboard">
              <BarChart3 size={20} />
           </button>
           <button onClick={handleExportAttendances} className="p-4 bg-white dark:bg-navy-900 text-slate-500 dark:text-slate-400 rounded-2xl hover:bg-neutral-50 dark:hover:bg-navy-800 transition-all shadow-sm border border-neutral-200 dark:border-navy-800 flex items-center gap-2 font-bold text-xs" title="Exportar Atendimentos">
              <Download size={20} /> {selectedAttendanceIds.size > 0 ? `(${selectedAttendanceIds.size})` : ''}
           </button>
           
           {selectedAttendanceIds.size > 0 && !isLeadership && onBulkDeleteAttendances && (
              <button 
                onClick={() => setShowBulkDeleteConfirm(true)} 
                className="p-4 bg-red-50 dark:bg-red-900/20 text-red-600 rounded-2xl hover:bg-red-100 dark:hover:bg-red-900/40 transition-all shadow-sm border border-red-100 dark:border-red-900/30 flex gap-2 font-bold text-xs items-center animate-in slide-in-from-right-2" 
                title="Excluir Selecionados"
              >
                  <Trash2 size={20} />
                  Excluir ({selectedAttendanceIds.size})
              </button>
           )}

           {currentUser.permissions.canEditAttendances && (
             <button onClick={onOpenCreate} className="bg-emerald-600 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl transition-all flex items-center gap-3">
               <Plus size={20} /> Novo Atendimento
             </button>
           )}
        </div>
      </div>

      <div className="bg-white dark:bg-navy-900 rounded-[32px] border border-slate-200 dark:border-navy-800 shadow-sm overflow-hidden mx-2">
        <div className="p-6 border-b border-slate-100 dark:border-navy-800 bg-slate-50/50 dark:bg-navy-950/30 flex flex-col xl:flex-row gap-4">
          <div className="relative group flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-emerald-600 transition-colors" size={20} />
            <input 
              placeholder="Pesquisar por título ou nome do cidadão..." 
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-6 py-3 bg-white dark:bg-navy-950 border border-slate-200 dark:border-navy-800 rounded-2xl text-sm font-medium focus:ring-2 focus:ring-emerald-600 outline-none dark:text-white"
            />
          </div>
          
          <div className="flex flex-wrap items-center gap-2">
            {/* Filtro de Responsável */}
            <div className="flex items-center gap-2 bg-white dark:bg-navy-950 px-4 py-3 rounded-2xl border border-slate-200 dark:border-navy-800 shadow-sm">
               <UserIcon size={16} className="text-slate-400" />
               <select 
                 value={selectedResponsibleId} 
                 onChange={e => setSelectedResponsibleId(e.target.value)}
                 className="bg-transparent border-none text-[10px] font-black uppercase tracking-widest outline-none dark:text-white cursor-pointer min-w-[100px]"
               >
                  <option value="all">Todos Resp.</option>
                  <option value="">Sem responsável</option>
                  {users.filter(u => u.role !== UserRole.LEADERSHIP).map(u => (
                    <option key={u.id} value={u.id}>{u.name.toUpperCase()}</option>
                  ))}
               </select>
            </div>

            {/* Filtro de Status */}
            <div className="flex items-center gap-2 bg-white dark:bg-navy-950 px-4 py-3 rounded-2xl border border-slate-200 dark:border-navy-800 shadow-sm">
               <Filter size={16} className="text-slate-400" />
               <select 
                 value={filterStatus} 
                 onChange={e => setFilterStatus(e.target.value)}
                 className="bg-transparent border-none text-[10px] font-black uppercase tracking-widest outline-none dark:text-white cursor-pointer min-w-[100px]"
               >
                  <option value="all">Todos Status</option>
                  {Object.values(AttendanceStatus).map(status => (
                    <option key={status} value={status}>{status.toUpperCase()}</option>
                  ))}
               </select>
            </div>

            {/* Filtro de Urgência */}
            <div className="flex items-center gap-2 bg-white dark:bg-navy-950 px-4 py-3 rounded-2xl border border-slate-200 dark:border-navy-800 shadow-sm">
               <AlertCircle size={16} className="text-slate-400" />
               <select 
                 value={filterUrgency} 
                 onChange={e => setFilterUrgency(e.target.value)}
                 className="bg-transparent border-none text-[10px] font-black uppercase tracking-widest outline-none dark:text-white cursor-pointer min-w-[100px]"
               >
                  <option value="all">Todas Urgências</option>
                  <option value="Baixa">BAIXA</option>
                  <option value="Média">MÉDIA</option>
                  <option value="Alta">ALTA</option>
                  <option value="Urgente">URGENTE</option>
               </select>
            </div>

            {/* Ordenação */}
            <button 
              onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
              className="flex items-center gap-2 bg-white dark:bg-navy-950 px-4 py-3 rounded-2xl border border-slate-200 dark:border-navy-800 shadow-sm hover:bg-slate-50 dark:hover:bg-navy-800 transition-colors"
              title="Ordenar por data de modificação"
            >
               <ArrowUpDown size={16} className="text-slate-400" />
               <span className="text-[10px] font-black uppercase tracking-widest dark:text-white">
                 {sortOrder === 'desc' ? 'Recentes' : 'Antigos'}
               </span>
            </button>
          </div>
        </div>

        {showDashboard && (() => {
          const all = sortedAttendances;
          const total = all.length;
          const pending = all.filter(a => a.status === 'Pendente').length;
          const done = all.filter(a => a.status === 'Concluído').length;
          const external = all.filter(a => a.source === 'public_form').length;

          const pieData = [
            { name: 'Pendente', value: pending, color: '#f59e0b' },
            { name: 'Em andamento', value: all.filter(a => a.status === 'Em andamento').length, color: '#3b82f6' },
            { name: 'Agendado', value: all.filter(a => a.status === 'Agendado').length, color: '#8b5cf6' },
            { name: 'Concluído', value: done, color: '#10b981' },
          ];

          const respMap: Record<string, number> = {};
          all.forEach(a => { const u = users.find(x => x.id === a.responsible_id); if (u) respMap[u.name] = (respMap[u.name] || 0) + 1; });
          const barData = Object.entries(respMap).map(([name, value]) => ({ name: name.split(' ')[0], value })).sort((a, b) => b.value - a.value);

          const monthMap: Record<string, number> = {};
          all.forEach(a => { if (a.created_at) { const m = a.created_at.substring(0, 7); monthMap[m] = (monthMap[m] || 0) + 1; } });
          const areaData = Object.entries(monthMap).sort(([a],[b]) => a.localeCompare(b)).slice(-6).map(([m, v]) => ({ name: m.substring(5) + '/' + m.substring(2, 4), value: v }));

          return (
            <SectionDashboard
              stats={[
                { label: 'Total', value: total, color: 'text-slate-500', icon: ClipboardList },
                { label: 'Pendentes', value: pending, color: 'text-amber-500', icon: Clock },
                { label: 'Concluídos', value: done, color: 'text-emerald-500', icon: CheckCircle2 },
                { label: 'Externos', value: external, color: 'text-indigo-500', icon: Globe },
              ]}
              pieData={pieData}
              pieTitle="Por Status"
              barData={barData}
              barTitle="Por Responsável"
              barColor="#10b981"
              areaData={areaData}
              areaTitle="Evolução Mensal"
            />
          );
        })()}

        {!showDashboard && <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50 dark:bg-navy-950/50 text-slate-400 text-[10px] uppercase tracking-[0.2em] font-black border-b border-slate-100">
                <th className="px-4 py-5 w-10 text-center">
                   <button onClick={toggleSelectAllAttendances} className="text-slate-400 hover:text-navy-900 dark:hover:text-white">
                      {selectedAttendanceIds.size > 0 && selectedAttendanceIds.size === filteredAttendances.length ? <CheckSquare size={18} /> : <Square size={18} />}
                   </button>
                </th>
                <th className="px-8 py-5 cursor-pointer hover:text-navy-900 dark:hover:text-white select-none" onClick={() => toggleListSort('status')}>Situação / Urgência {listSortCol === 'status' ? (listSortDir === 'asc' ? '↑' : '↓') : ''}</th>
                <th className="px-8 py-5 cursor-pointer hover:text-navy-900 dark:hover:text-white select-none" onClick={() => toggleListSort('title')}>Título {listSortCol === 'title' ? (listSortDir === 'asc' ? '↑' : '↓') : ''}</th>
                <th className="px-8 py-5 cursor-pointer hover:text-navy-900 dark:hover:text-white select-none" onClick={() => toggleListSort('requester')}>Solicitante {listSortCol === 'requester' ? (listSortDir === 'asc' ? '↑' : '↓') : ''}</th>
                <th className="px-8 py-5 cursor-pointer hover:text-navy-900 dark:hover:text-white select-none" onClick={() => toggleListSort('responsible')}>Responsável {listSortCol === 'responsible' ? (listSortDir === 'asc' ? '↑' : '↓') : ''}</th>
                <th className="px-8 py-5 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-navy-800">
              {sortedAttendances.map(a => {
                const contact = contacts.find(c => c.id === a.contactid);
                const responsible = users.find(u => u.id === a.responsible_id);
                const isCreator = a.created_by === currentUser.id || a.origin_details === currentUser.id;
                const canEdit = !isLeadership || (currentUser.permissions.canEditOwnContent && isCreator);
                const isUnviewedExternal = a.source === 'public_form' && !a.viewed_at;

                return (
                  <tr key={a.id} onClick={() => { setSelectedAttendanceId(a.id); if (isUnviewedExternal) onUpdateAttendance(a.id, { viewed_at: new Date().toISOString() }); }} className={`hover:bg-slate-50 dark:hover:bg-navy-950/50 transition-colors cursor-pointer group ${selectedAttendanceIds.has(a.id) ? 'bg-slate-50 dark:bg-navy-950/30' : ''}`}>
                    <td className="px-4 py-6 text-center" onClick={(e) => e.stopPropagation()}>
                       <button onClick={(e) => toggleSelectAttendance(a.id, e)} className="text-slate-300 hover:text-emerald-600">
                          {selectedAttendanceIds.has(a.id) ? <CheckSquare size={18} className="text-emerald-600" /> : <Square size={18} />}
                       </button>
                    </td>
                    <td className="px-8 py-6">
                      <div className="flex flex-col gap-2">
                        <span className={`px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest border w-fit ${getStatusColor(a.status)}`}>{a.status}</span>
                        <span className="px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest border w-fit bg-slate-100 text-slate-600">{a.urgency}</span>
                      </div>
                    </td>
                    <td className="px-8 py-6">
                      <div className="flex items-center gap-2">
                        {isUnviewedExternal && <span className="px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest bg-red-500 text-white animate-pulse">Novo</span>}
                        <p className="text-sm font-black text-navy-950 dark:text-white group-hover:text-emerald-600 transition-colors line-clamp-1">{a.demand}</p>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                         <Calendar size={12} /> {new Date(a.created_at).toLocaleDateString('pt-BR')}
                         {a.source === 'public_form' && <span className="flex items-center gap-1 text-emerald-600"><Globe size={12}/> Formulário Externo</span>}
                      </div>
                    </td>
                    <td className="px-8 py-6">
                       <p className="text-xs font-bold text-slate-700 dark:text-slate-300">{contact?.name || 'Não identificado'}</p>
                    </td>
                    <td className="px-8 py-6">
                       <div className="flex items-center gap-2">
                          <div className={`w-7 h-7 rounded-lg flex items-center justify-center font-black text-[10px] ${responsible ? 'bg-parliament-50 text-parliament-600' : 'bg-slate-100 text-slate-400'}`}>
                             {responsible?.name.charAt(0) || '?'}
                          </div>
                          <p className={`text-xs font-bold ${responsible ? 'text-slate-700 dark:text-slate-200' : 'text-amber-500 italic'}`}>
                             {responsible?.name || 'Não atribuído'}
                          </p>
                       </div>
                    </td>
                    <td className="px-8 py-6 text-right">
                       <div className="flex items-center justify-end gap-2">
                          {currentUser.permissions.canEditAttendances && !isLeadership && (
                            <button 
                              onClick={(e) => { 
                                e.stopPropagation(); 
                                setDeleteSingleId(a.id);
                              }}
                              className="p-2 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                            >
                              <Trash2 size={18} />
                            </button>
                          )}
                          <button className="p-2.5 text-slate-300 group-hover:text-emerald-600 transition-all">
                             {canEdit ? <Edit2 size={18} /> : <ChevronRight size={18} />}
                          </button>
                       </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>}
      </div>

      {selectedAttendance && (
        <AttendanceDetailModal 
          attendance={selectedAttendance}
          users={users}
          contacts={contacts}
          onClose={() => setSelectedAttendanceId(null)}
          onUpdateAttendance={onUpdateAttendance}
          onDeleteAttendance={async (id) => { onDeleteAttendance(id); return true; }}
          currentUser={currentUser}
        />
      )}
    </div>
  );
};

export default AttendanceList;
